import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import {
  PapiDaSharedLessLike,
  PapiDaSharedLessLikeUndo,
  PapiRequestDaSharedLessLike,
  PapiRequestDaSharedLessLikeUndo,
} from '@wix/da-papi-types';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { actions as notificationActions } from '@wix/da-shared-react/pkg/Notifications';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { deleteItem } from '@wix/da-shared-react/pkg/Stream/base/actions';
import {
  insertItemsAtOffsetAndSetCursor,
  setCursor,
} from '@wix/da-shared-react/pkg/Stream/withCursor/actions';
import { showLessLikeThis, undoShowLessLikeThis } from '../actions/recommendation';
import { NotificationType } from '../types/notification';
import { PAGE_STREAM_ID } from '../../constants';
import { getCurrentCursor } from '../selectors/stream';
import i18next from 'i18next';

function* handleShowLessLikeThis(action: ReturnType<typeof showLessLikeThis>) {
  const { deviationId, reason, typeId, ownerId } = action.payload;
  const cursor = yield select(getCurrentCursor);
  const data: PapiRequestDaSharedLessLike = {
    itemid: deviationId,
    typeid: typeId,
    reason_shown: reason,
    ownerid: ownerId?.toString(),
    cursor,
  };
  let result: PapiDaSharedLessLike | false;
  try {
    result = yield call(requestPuppy, {
      method: 'post',
      url: 'less_like',
      data,
    });
  } catch {
    yield putErrorNotification(i18next.t('watch.remove.error'));
    return;
  }

  if (!result || isApiError(result)) {
    yield putErrorNotification(i18next.t('watch.remove.error'));
    return;
  }

  yield put(notificationActions.addNotification(NotificationType.SHOW_LESS, action.payload));

  const streamItemId = typeId === ResourceType.TIER ? `${typeId}-${deviationId}` : deviationId;

  yield put(
    deleteItem({
      streamId: PAGE_STREAM_ID,
      item: streamItemId,
      doNotUpdateNextOffset: true,
    })
  );

  if (result.updatedCursor) {
    yield put(setCursor(PAGE_STREAM_ID, result.updatedCursor));
  }
}

function* handleUndoShowLessLikeThis(action: ReturnType<typeof undoShowLessLikeThis>) {
  const { deviationId, offset, reason, typeId, ownerId } = action.payload;
  const cursor = yield select(getCurrentCursor);

  const data: PapiRequestDaSharedLessLikeUndo = {
    itemid: deviationId,
    typeid: typeId,
    reason_shown: reason,
    ownerid: ownerId?.toString(),
    cursor,
  };
  let result: PapiDaSharedLessLikeUndo | false;
  try {
    result = yield call(requestPuppy, {
      method: 'post',
      url: 'less_like_undo',
      data,
    });
  } catch {
    yield putErrorNotification(i18next.t('watch.undoRemove.error'));
    return;
  }

  if (!result || isApiError(result)) {
    yield putErrorNotification(i18next.t('watch.undoRemove.error'));
    return;
  }

  const streamItemId = typeId === ResourceType.TIER ? `${typeId}-${deviationId}` : deviationId;

  yield put(
    insertItemsAtOffsetAndSetCursor({
      streamId: PAGE_STREAM_ID,
      items: [streamItemId],
      offset,
      newCursor: result.updatedCursor,
    })
  );
}

export default function* recommendation() {
  yield all([
    takeEvery(showLessLikeThis, handleShowLessLikeThis),
    takeEvery(undoShowLessLikeThis, handleUndoShowLessLikeThis),
  ]);
}
